<template>
  <v-app v-if="state === 'success'" class="app">
    <YesNoDialog
      :title="$t('projectRestoreResult')"
      v-model="restoreProjectResultDialog"
      hide-no-button
      :yes-button-title="$t('close')"
      :max-width="400"
    >
      <div class="pt-3" v-if="restoreProjectResult">
        <v-alert dense outlined type="success">
          {{ $t('projectWithNameRestored', { projectName: restoreProjectResult.projectName }) }}
        </v-alert>

        <v-alert dense outlined type="error" class="mb-0">
          <b>{{ $t('emptyKeysRestored', { emptyKeys: restoreProjectResult.emptyKeys }) }}</b>
          {{ $t('pleaseUpdateAccessKeys') }}
        </v-alert>
      </div>
    </YesNoDialog>

    <EditDialog
      v-model="userDialog"
      save-button-text="Save"
      :title="$t('editUser')"
      v-if="user"
      event-name="i-user"
      :hide-buttons="hideUserDialogButtons"
    >
      <template v-slot:form="{ onSave, onError, needSave, needReset }">
        <UserForm
          :project-id="projectId"
          :item-id="user.id"
          @save="onSave"
          @error="onError"
          :need-save="needSave"
          :need-reset="needReset"
          :is-admin="user.admin"
          :auth-methods="(systemInfo || { auth_methods: {} }).auth_methods"
          :login-with-password="(systemInfo || {}).login_with_password"
          @hide-action-buttons="hideUserDialogButtons = true"
          @show-action-buttons="hideUserDialogButtons = false"
        />
      </template>
    </EditDialog>

    <TaskLogDialog
      v-model="taskLogDialog"
      @close="onTaskLogDialogClosed()"
      :project-id="projectId"
      :item-id="taskId"
      :system-info="systemInfo"
    />

    <EditDialog
      v-model="newProjectDialog"
      save-button-text="Create"
      :title="$t('newProject')"
      event-name="i-project"
      @close="onNewProjectDialogueClosed()"
    >
      <template v-slot:form="{ onSave, onError, needSave, needReset }">
        <ProjectForm
          v-if="newProjectType === ''"
          item-id="new"
          @save="onSave"
          @error="onError"
          :need-save="needSave"
          :need-reset="needReset"
        />
      </template>
    </EditDialog>

    <EditDialog
      v-model="restoreProjectDialog"
      save-button-text="Restore"
      :title="$t('restoreProject')"
      event-name="i-project"
    >
      <template v-slot:form="{ onSave, onError, needSave, needReset }">
        <RestoreProjectForm
          item-id="new"
          @save="onSave"
          @error="onError"
          :need-save="needSave"
          :need-reset="needReset"
        />
      </template>
    </EditDialog>

    <SystemInfoDialog v-model="systemInfoDialog" v-if="user && user.admin" />

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <v-btn text @click="snackbar = false">
        {{ $t('close') }}
      </v-btn>
    </v-snackbar>

    <v-navigation-drawer
      app
      fixed
      width="292"
      v-model="drawer"
      mobile-breakpoint="960"
      v-if="$route.path.startsWith('/project/')"
      class="NavDrawer"
    >
      <!-- ZetaPilot brand mark at the top of the project drawer.
           Mechanical / aviation lockup: instrument-style wordmark,
           a tiny "TYPE" classification, and a tabular build callsign. -->
      <div class="app__brand-bar">
        <svg
          class="app__brand-logo"
          viewBox="0 0 64 64"
          fill="none"
          role="img"
          aria-label="ZetaPilot"
        >
          <g stroke="currentColor" stroke-width="4" stroke-linejoin="round">
            <rect x="13" y="8" width="38" height="48" rx="4" fill="none" />
            <rect x="20" y="17" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
            <rect x="20" y="26" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
            <rect x="20" y="35" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
            <rect x="20" y="44" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
          </g>
        </svg>
        <div class="app__brand-lockup">
          <span class="app__brand-name">ZetaPilot</span>
          <span class="app__brand-meta">
            <span class="app__brand-classifier">{{ $t('brandClassifier') }}</span>
            <span class="app__brand-callsign">ZP-01</span>
          </span>
        </div>
      </div>
      <v-divider class="app__brand-divider" />

      <v-menu bottom max-width="235" max-height="100%" v-if="project">
        <template v-slot:activator="{ on, attrs }">
          <v-list class="pa-0 overflow-y-auto app__project-list">
            <v-list-item
              key="project"
              class="app__project-selector"
              v-bind="attrs"
              v-on="on"
              data-testid="sidebar-currentProject"
            >
              <v-list-item-icon>
                <v-avatar
                  :color="getProjectColor(project)"
                  size="24"
                  style="font-size: 13px; font-weight: bold"
                >
                  <span class="white--text">{{ getProjectInitials(project) }}</span>
                </v-avatar>
              </v-list-item-icon>

              <v-list-item-content>
                <span class="app__project-kicker">{{ $t('currentProject') }}</span>
                <v-list-item-title class="app__project-selector-title">
                  {{ project.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="app__project-selector-role">
                  {{ userRole.role }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-icon>
                <ZpIcon name="chevron-down" :size="20" />
              </v-list-item-icon>
            </v-list-item>
          </v-list>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, i) in projects"
            :key="i"
            :to="`/project/${item.id}`"
            @click="selectProject(item.id)"
          >
            <v-list-item-icon>
              <v-avatar
                :color="getProjectColor(item)"
                size="24"
                style="font-size: 13px; font-weight: bold"
              >
                <span class="white--text">{{ getProjectInitials(item) }}</span>
              </v-avatar>
            </v-list-item-icon>
            <v-list-item-content>{{ item.name }}</v-list-item-content>
          </v-list-item>

          <v-divider v-if="user.can_create_project" />

          <v-list-item
            @click="showNewProjectDialogue()"
            v-if="user.can_create_project"
            data-testid="sidebar-newProject"
          >
            <v-list-item-icon>
              <ZpIcon name="plus" :size="20" />
            </v-list-item-icon>

            <v-list-item-content>
              {{ $t('newProject2') }}
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            @click="restoreProjectDialog = true"
            v-if="user.can_create_project"
            data-testid="sidebar-restoreProject"
          >
            <v-list-item-icon>
              <ZpIcon name="archive-restore" :size="20" />
            </v-list-item-icon>

            <v-list-item-content>
              {{ $t('restoreProject') }}
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-list class="pt-0" v-if="!project">
        <v-list-item key="new_project" :to="`/project/new`">
          <v-list-item-icon>
            <ZpIcon name="plus" :size="20" />
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ $t('newProject2') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item key="restore_project" :to="`/project/restore`">
          <v-list-item-icon>
            <ZpIcon name="rotate-ccw" :size="20" />
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ $t('restoreProject') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <nav v-if="project" class="NavDrawer__nav" :aria-label="$t('projectNavigation')">
        <section
          v-for="group in projectNavGroups"
          :key="group.key"
          class="NavDrawer__section"
        >
          <div class="NavDrawer__section-label">{{ $t(group.label) }}</div>
          <v-list dense class="NavDrawer__list">
            <v-list-item
              v-for="item in group.items"
              :key="item.key"
              :to="item.to"
              :data-testid="item.testId || null"
              class="NavDrawer__item"
            >
              <span class="NavDrawer__item-code zp-mono">{{ item.code }}</span>
              <span class="NavDrawer__item-icon">
                <ZpIcon :name="item.icon" :size="18" />
              </span>
              <span class="NavDrawer__item-label">{{ $t(item.label) }}</span>
            </v-list-item>
          </v-list>
        </section>
      </nav>

      <template v-slot:append>
        <v-list class="pa-0 NavDrawer__footer">
          <v-list-item class="NavDrawer__footer-controls">
            <v-btn-toggle
              v-model="themeMode"
              mandatory
              dense
              borderless
              class="ThemeToggle"
            >
              <v-btn value="system" small :title="$t('theme_system')">
                <ZpIcon name="monitor" :size="16" />
              </v-btn>
              <v-btn value="light" small :title="$t('theme_light')">
                <ZpIcon name="sun" :size="16" />
              </v-btn>
              <v-btn value="dark" small :title="$t('theme_dark')">
                <ZpIcon name="moon" :size="16" />
              </v-btn>
            </v-btn-toggle>

            <v-spacer />

            <v-menu top min-width="150" max-width="235" nudge-top="12" :position-x="50" absolute>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <img
                    style="border-radius: 30px; max-width: 100%"
                    :src="`flags/${lang.flag}.svg`"
                    alt=""
                  />
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="lang in languages"
                  :key="lang.id"
                  @click="selectLanguage(lang.id)"
                >
                  <v-list-item-icon>
                    <v-img
                      style="border-radius: 20px; max-width: 24px"
                      :src="`flags/${lang.flag}.svg`"
                      alt=""
                    />
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>{{ lang.title }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-list-item>

          <v-menu
            top
            offset-y
            min-width="235"
            max-width="235"
            max-height="calc(100vh - 32px)"
            nudge-top="8"
            content-class="UserMenu"
            transition="slide-y-reverse-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-list-item
                key="project"
                v-bind="attrs"
                v-on="on"
                class="NavDrawer__user"
              >
                <v-list-item-icon>
                  <ZpIcon v-if="user.pro" name="badge-check" :size="20" color="#f14668" />
                  <ZpIcon v-else name="circle-user" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ user.name }}
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-chip color="red" v-if="user.admin" small>{{ $i18n.t('admin') }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>

            <v-list>
              <v-list-item key="system-info" v-if="user.admin" @click="systemInfoDialog = true">
                <v-list-item-icon>
                  <ZpIcon name="server" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('systemInfo') }}
                </v-list-item-content>
              </v-list-item>

              <v-list-item v-else key="version">
                <v-list-item-icon>
                  <ZpIcon name="info" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ systemInfo.version }}
                </v-list-item-content>
              </v-list-item>

              <!-- ZetaPilot is a corporate self-hosted product; no
                   subscription upsell, no Semaphore billing portal. -->

              <v-divider />

              <v-list-item key="runners" to="/runners" v-if="user.admin">
                <v-list-item-icon>
                  <ZpIcon name="settings" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('runners') }}
                </v-list-item-content>
              </v-list-item>

              <v-list-item key="tasks" to="/tasks" v-if="user.admin">
                <v-list-item-icon>
                  <ZpIcon name="check-check" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('activeTasks') }}
                </v-list-item-content>
              </v-list-item>

              <v-list-item key="tokens" to="/tokens" data-testid="sidebar-tokens">
                <v-list-item-icon>
                  <ZpIcon name="terminal" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('api_tokens') }}
                </v-list-item-content>
              </v-list-item>

              <v-list-item key="users" to="/users" v-if="user.admin">
                <v-list-item-icon>
                  <ZpIcon name="users" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('users') }}
                </v-list-item-content>
              </v-list-item>

              <v-list-item key="roles" to="/roles" v-if="isPro && user.admin">
                <v-list-item-icon>
                  <ZpIcon name="user-cog" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('Roles') }}
                </v-list-item-content>
              </v-list-item>

              <v-list-item key="edit" @click="userDialog = true">
                <v-list-item-icon>
                  <ZpIcon name="pencil" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('editAccount') }}
                </v-list-item-content>
              </v-list-item>

              <v-divider />

              <v-list-item key="sign_out" @click="signOut()" data-testid="sidebar-signout">
                <v-list-item-icon>
                  <ZpIcon name="log-out" :size="20" />
                </v-list-item-icon>

                <v-list-item-content>
                  {{ $t('signOut') }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-main>
      <router-view
        :projectId="projectId"
        :projectType="(project || {}).type || ''"
        :userPermissions="(userRole || {}).permissions"
        :userRole="(userRole || {}).role"
        :userId="(user || {}).id"
        :isAdmin="(user || {}).admin"
        :user="user"
        :premiumFeatures="(systemInfo || { premium_features: {} }).premium_features"
        :authMethods="(systemInfo || { auth_methods: {} }).auth_methods"
        :systemInfo="systemInfo"
      ></router-view>
    </v-main>
  </v-app>
  <v-app v-else-if="state === 'loading'">
    <v-main>
      <v-container fluid fill-height align-center justify-center class="pa-0">
        <v-progress-circular :size="70" color="primary" indeterminate></v-progress-circular>
      </v-container>
    </v-main>
  </v-app>
  <v-app v-else-if="state === 'error'">
    <v-main>
      <v-container
        fluid
        flex-column
        fill-height
        align-center
        justify-center
        class="pa-0 text-center"
      >
        <v-alert text color="error" class="d-inline-block">
          <h3 class="headline">
            {{ $t('error') }}
          </h3>
          {{ snackbarText }}
        </v-alert>
        <div class="mb-6">
          <v-btn text color="blue darken-1" @click="refreshPage()">
            <ZpIcon name="refresh-cw" :size="18" class="mr-2" />
            {{ $t('refreshPage') }}
          </v-btn>
          <v-btn text color="blue darken-1" @click="signOut()">
            <ZpIcon name="log-out" :size="18" class="mr-2" />
            {{ $t('relogin') }}
          </v-btn>
        </div>
      </v-container>
    </v-main>
  </v-app>
  <v-app v-else></v-app>
</template>
<style lang="scss">
// Cockpit drawer — neutral surface tinted toward the brand hue (the
// neutrals already pick up indigo through the design-token chain). Both
// light and dark themes inherit their own --zp-bg-surface; the drawer
// reads as "elevated" relative to the page canvas without resorting to a
// saturated brand block.
.NavDrawer {
  height: 100dvh !important;
  background: var(--zp-bg-surface) !important;
  border-right: var(--zp-border-hair) solid var(--zp-border-hairline) !important;
  color: var(--zp-text-primary);

  // Vuetify's nav-drawer wrapper carries the colour we asked for; null it
  // out so our token-driven surface wins regardless of theme.
  .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    background: transparent !important;
  }

  // List items: secondary text on idle, primary on hover, active gets a
  // brand-tinted background + brand-coloured icon. The default chrome
  // (router-link active state) provides the "you are here" cue.
  .v-list-item {
    color: var(--zp-text-secondary);
    border-radius: var(--zp-radius-sm);
    transition:
      background-color var(--zp-duration-fast) var(--zp-ease-out),
      color var(--zp-duration-fast) var(--zp-ease-out);

    .v-list-item__title {
      color: var(--zp-text-primary);
    }

    &:hover:not(.v-list-item--active) {
      background: var(--zp-bg-subtle) !important;
      color: var(--zp-text-primary);
    }

    &.v-list-item--active {
      // Stronger wash so it's still distinguishable from hover state.
      background: var(--zp-bg-muted) !important;
      color: var(--zp-brand);

      &::before {
        opacity: 0 !important;
      }

      .v-list-item__title,
      .v-list-item__icon,
      .zp-icon {
        color: var(--zp-brand);
      }

      // Hover-on-active: re-introduce Vuetify's ::before overlay so
      // there's tactile feedback even when the row is the current page.
      &:hover::before {
        opacity: 0.06 !important;
      }
    }
  }

  .v-list-item__icon .zp-icon {
    color: var(--zp-text-tertiary);
  }

  .v-divider {
    border-color: var(--zp-border-hairline) !important;
  }
}

.NavDrawer__nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 12px 18px;
}

.NavDrawer__section + .NavDrawer__section {
  margin-top: 18px;
}

.NavDrawer__section-label {
  padding: 0 8px 7px;
  font-size: var(--zp-text-2xs);
  line-height: 1;
  letter-spacing: var(--zp-tracking-overline);
  text-transform: uppercase;
  font-weight: 700;
  color: var(--zp-text-tertiary);
}

.NavDrawer__list {
  padding: 0 !important;
  background: transparent !important;
}

.NavDrawer__item {
  position: relative;
  display: grid !important;
  grid-template-columns: 28px 24px minmax(0, 1fr);
  align-items: center;
  column-gap: 10px;
  height: 38px !important;
  min-height: 38px !important;
  margin: 2px 0;
  padding: 0 10px !important;

  &::before {
    border-radius: var(--zp-radius-sm);
  }
}

.NavDrawer__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--zp-text-tertiary);

  .zp-icon {
    display: block;
  }
}

.NavDrawer__item-code {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 28px;
  height: 24px;
  font-size: 0.625rem;
  line-height: 1;
  letter-spacing: 0;
  color: var(--zp-text-tertiary);
}

.NavDrawer__item-label {
  display: block;
  min-width: 0;
  font-size: var(--zp-text-sm);
  line-height: 18px;
  font-weight: 650;
  color: var(--zp-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.NavDrawer__item.v-list-item--active {
  background: var(--zp-brand-soft) !important;
  color: var(--zp-brand);

  .NavDrawer__item-code,
  .NavDrawer__item-icon,
  .NavDrawer__item-label,
  .zp-icon {
    color: var(--zp-brand) !important;
  }
}

.NavDrawer__footer {
  flex: 0 0 auto;
  padding: 10px 12px 12px !important;
  border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
  background: var(--zp-bg-elevated) !important;
}

.NavDrawer__footer-controls {
  min-height: 40px !important;
  padding: 0 4px !important;

  .v-btn img {
    width: 22px;
    height: 22px;
    border-radius: var(--zp-radius-pill);
  }
}

.NavDrawer__user {
  min-height: 42px !important;
  margin-top: 8px;
  padding: 0 8px !important;
  border: var(--zp-border-hair) solid var(--zp-border-hairline);
  background: var(--zp-bg-surface);

  .v-list-item__title {
    font-size: var(--zp-text-sm);
    font-weight: 700;
  }

  .v-chip {
    height: 22px;
    border-radius: var(--zp-radius-md);
    font-size: var(--zp-text-xs);
    font-weight: 700;
  }
}

.theme--dark {
  --highlighted-card-bg-color: #262626;
}

.theme--light {
  --highlighted-card-bg-color: #f3f3f3;
}

// User menu — anchored above the user row in the drawer. Caps height to
// the viewport so a tall menu (9+ items on admins) never bleeds off-screen
// on short displays; the inner list scrolls instead.
.UserMenu {
  overflow-y: auto;

  .v-list {
    padding: 4px 0;
  }
}

.ThemeToggle {
  background: transparent !important;
  border-radius: var(--zp-radius-md, 6px) !important;

  .v-btn {
    min-width: 32px !important;
    height: 32px !important;
    padding: 0 8px !important;
    border-radius: 0 !important;
    background: transparent !important;
    border: none !important;
    color: var(--zp-text-tertiary, #888) !important;
    opacity: 1 !important;

    &:hover {
      color: var(--zp-text-primary, inherit) !important;
      background: var(--zp-bg-subtle, rgba(133, 133, 133, 0.08)) !important;
    }

    &.v-btn--active {
      color: var(--zp-brand, #7079D6) !important;
      background: var(--zp-bg-subtle, rgba(112, 121, 214, 0.1)) !important;

      &::before {
        opacity: 0 !important;
      }
    }
  }
}

.v-dialog > .v-card > .v-card__title {
  flex-wrap: nowrap;
  overflow: hidden;

  & * {
    white-space: nowrap;
  }
}

.v-data-table tbody tr.v-data-table__expanded__content {
  box-shadow: none !important;
}

.v-data-table a {
  text-decoration-line: none;

  &:hover {
    text-decoration-line: underline;
  }
}

.breadcrumbs__item--link {
  text-decoration-line: none;

  &:hover {
    text-decoration-line: underline;
  }
}

.breadcrumbs__separator {
  padding: 0 10px;
}

// ZetaPilot brand bar — cockpit lockup at the top of the project drawer.
.app__brand-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 14px;
  color: var(--zp-text-primary);
}

.app__brand-logo {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  color: var(--zp-brand);
}

.app__brand-lockup {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.app__brand-name {
  font-family: var(--zp-font-display);
  font-weight: 700;
  font-size: 1.0625rem;
  letter-spacing: -0.005em;
  line-height: 1;
  color: var(--zp-text-primary);
}

.app__brand-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.625rem;
  letter-spacing: var(--zp-tracking-overline);
  text-transform: uppercase;
  font-weight: 600;
  color: var(--zp-text-tertiary);
  line-height: 1;
}

.app__brand-classifier {
  letter-spacing: 0.1em;
}

.app__brand-callsign {
  font-family: var(--zp-font-mono);
  color: var(--zp-text-secondary);
  padding: 1px 4px;
  border: var(--zp-border-hair) solid var(--zp-border-hairline);
  border-radius: 2px;
}

.app__brand-divider {
  border-color: var(--zp-border-hairline) !important;
}

.app__project-list {
  padding: 10px 12px 12px !important;
  background: var(--zp-bg-elevated) !important;
}

.app__project-selector {
  min-height: 72px !important;
  padding: 0 10px !important;
  border: var(--zp-border-hair) solid var(--zp-border-hairline);
  background: var(--zp-bg-surface);

  & > .v-list-item__content {
    padding: 0;
  }

  .v-list-item__icon {
    margin-top: 22px !important;
  }
}

.app__project-kicker {
  display: block;
  margin-bottom: 4px;
  color: var(--zp-text-tertiary);
  font-size: var(--zp-text-2xs);
  line-height: 1;
  letter-spacing: var(--zp-tracking-overline);
  text-transform: uppercase;
  font-weight: 700;
}

.app__project-selector-title {
  font-family: var(--zp-font-display);
  font-size: var(--zp-text-md) !important;
  font-weight: 700;
  line-height: var(--zp-leading-tight);
}

.app__project-selector-role {
  margin-top: 3px;
  color: var(--zp-text-secondary) !important;
  font-size: var(--zp-text-xs) !important;
  line-height: var(--zp-leading-snug) !important;
  font-weight: 650;
  letter-spacing: var(--zp-tracking-label);
  text-transform: uppercase;
}

.v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 16px !important;
}

.v-toolbar__content {
  height: 64px !important;
}

.v-data-table .v-data-footer {
  margin-left: 16px !important;
  margin-right: 16px !important;
}

.v-data-table__wrapper {
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.v-data-table {
  td:first-child,
  th:first-child {
    padding-left: 2px !important;
  }

  td:last-child,
  th:last-child {
    padding-right: 2px !important;
  }

  .v-data-table__wrapper > table > thead > tr:last-child > th {
    text-transform: uppercase;
    white-space: nowrap;
  }

  .v-data-table__wrapper > table > tbody > tr {
    background: transparent !important;

    &:hover {
      background-color: rgba(143, 143, 143, 0.04) !important;
    }

    & > td {
      white-space: nowrap;
    }
  }
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > th,
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  font-size: 1rem !important;
}

.v-data-footer {
  font-size: 1rem !important;
}

.v-toolbar__title {
  font-weight: bold !important;
}

.v-app-bar__nav-icon {
  margin-left: 0 !important;
}

.v-toolbar__title:not(:first-child) {
  margin-left: 10px !important;
}

.v-slide-group__prev--disabled {
  display: none !important;
}

@media (min-width: 960px) {
  .v-app-bar__nav-icon {
    display: none !important;
  }

  .v-toolbar__title:not(:first-child) {
    padding-left: 0 !important;
    margin-left: 0 !important;
  }
}

.v-input {
  .v-input__slot fieldset {
    border-radius: 8px;
    border-width: 1px;
    border-color: rgba(133, 133, 133, 0.4);
    background-color: rgba(133, 133, 133, 0.1);
  }

  .v-label--active {
    text-shadow: 0 0 2px black;
    font-weight: 500;
  }

  &.primary--text {
    .v-input__slot fieldset {
      border-width: 2px;
      border-color: #2196f3;
    }
  }

  &.error--text {
    .v-input__slot fieldset {
      border-width: 2px;
      border-color: #ff5252;
    }
  }
}

.v-input--is-disabled {
  opacity: 0.5;
}

.theme--light {
  .v-input {
    .v-label--active {
      text-shadow: 0 0 2px white;
    }
  }
}

.v-list--dense .v-list-item .v-list-item__title {
  font-weight: normal;
  font-size: 1rem;
}

@import '~vuetify/src/styles/styles.sass';
@media #{map-get($display-breakpoints, 'xl-only')} {
  .CenterToScreen {
    transform: translateX(-130px);
  }
}
</style>

<script>
import axios from 'axios';
import { getErrorMessage } from '@/lib/error';
import EditDialog from '@/components/EditDialog.vue';
import ProjectForm from '@/components/ProjectForm.vue';
import UserForm from '@/components/UserForm.vue';
import EventBus from '@/event-bus';
import socket from '@/socket';

import RestoreProjectForm from '@/components/RestoreProjectForm.vue';
import YesNoDialog from '@/components/YesNoDialog.vue';
import TaskLogDialog from '@/components/TaskLogDialog.vue';
import SystemInfoDialog from '@/components/SystemInfoDialog.vue';
import delay from '@/lib/delay';

const PROJECT_COLORS = ['red', 'blue', 'orange', 'green'];

const LANGUAGES = {
  en: {
    title: 'English',
  },
  es: {
    title: 'Español',
  },
  ru: {
    title: 'Russian',
  },
  de: {
    title: 'German',
  },
  nl: {
    title: 'Dutch (Netherlands)',
  },
  zh_cn: {
    title: '中文(大陆)',
  },
  zh_tw: {
    title: '中文(台灣)',
  },
  fr: {
    title: 'French',
  },
  it: {
    title: 'Italian',
  },
  pl: {
    title: 'Polish',
  },
  pt: {
    title: 'Portuguese',
  },
  pt_br: {
    title: 'Português do Brasil',
  },
};

function getLangInfo(locale) {
  let lang = locale;
  let res = LANGUAGES[lang];

  // failback short i18n
  if (!res) {
    lang = lang.split('_')[0];
    res = LANGUAGES[lang];
  }

  if (!res) {
    lang = 'en';
    res = LANGUAGES[lang];
  }

  // Spread before stamping — otherwise we'd write `flag` directly onto the
  // shared LANGUAGES[lang] object and corrupt the canonical entry for
  // subsequent calls with different unknown locales.
  return { ...res, flag: lang };
}

function getSystemLang() {
  // /g — see plugins/i18.js comment; replace('-', '_') only swaps first.
  const locale = navigator.language.replace(/-/g, '_').toLocaleLowerCase();

  return getLangInfo(locale || 'en');
}

export default {
  name: 'App',
  components: {
    TaskLogDialog,
    YesNoDialog,
    RestoreProjectForm,
    UserForm,
    EditDialog,
    ProjectForm,
    SystemInfoDialog,
  },
  data() {
    return {
      drawer: null,
      user: null,
      userRole: null,
      systemInfo: null,
      state: 'loading',
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      projects: null,
      newProjectDialog: null,
      newProjectType: '',
      userDialog: null,
      hideUserDialogButtons: false,

      systemInfoDialog: null,

      restoreProjectDialog: null,
      restoreProjectResult: null,
      restoreProjectResultDialog: null,

      taskLogDialog: null,
      taskId: null,
      template: null,
      // 'system' | 'light' | 'dark'. Tracks the OS preference when 'system'.
      themeMode: 'system',
      mediaQuery: null,
      languages: [
        {
          id: '',
          flag: getSystemLang().flag,
          title: 'System',
        },
        ...Object.keys(LANGUAGES).map((lang) => ({
          id: lang,
          flag: lang,
          ...LANGUAGES[lang],
        })),
      ],
    };
  },

  watch: {
    async projects(val) {
      if (
        val.length === 0
        && this.$route.path.startsWith('/project/')
        && this.$route.path !== '/project/new'
        && this.$route.path !== '/project/premium'
      ) {
        if (this.$route.query.new_project === 'premium') {
          await this.$router.push({ path: '/project/premium' });
        } else {
          await this.$router.push({ path: '/project/new' });
        }
      }
    },

    async $route(val) {
      if (val.query.t == null) {
        this.taskLogDialog = false;
      } else {
        const taskId = parseInt(this.$route.query.t || '', 10);
        if (taskId) {
          EventBus.$emit('i-show-task', { taskId });
        }
      }

      if ((this.projects || []).length > 0 && this.$route.query.new_project) {
        EventBus.$emit('i-new-project', { projectType: this.$route.query.new_project });
      }
    },

    themeMode(val) {
      localStorage.setItem('themeMode', val);
      this.applyTheme();
    },
  },

  computed: {
    // ZetaPilot is the corporate distribution: all Pro features are always
    // on, no subscription gating. Upstream builds toggled this via the
    // `pro_*` VUE_APP_BUILD_TYPE; we keep the computed so call-sites that
    // gate runners/roles/etc. on `isPro` continue to work.
    isPro() {
      return true;
    },

    lang() {
      const locale = localStorage.getItem('lang');

      if (!locale) {
        return getSystemLang();
      }

      return getLangInfo(locale || 'en');
    },

    projectId() {
      return parseInt(this.$route.params.projectId, 10) || null;
    },

    project() {
      if (this.projects == null) {
        return null;
      }
      return this.projects.find((x) => x.id === this.projectId);
    },

    templatesUrl() {
      let viewId = localStorage.getItem(`project${this.projectId}__lastVisitedViewId`);
      if (viewId) {
        viewId = parseInt(viewId, 10);
        if (!Number.isNaN(viewId)) {
          return `/project/${this.projectId}/views/${viewId}/templates`;
        }
      }
      return `/project/${this.projectId}/templates`;
    },

    projectNavGroups() {
      const base = `/project/${this.projectId}`;
      const isAutomationProject = this.project && this.project.type === '';

      const groups = [
        {
          key: 'operations',
          label: 'navGroupOperations',
          items: [
            {
              key: 'dashboard',
              code: 'OPS',
              icon: 'layout-dashboard',
              label: 'dashboard',
              to: `${base}/history`,
              testId: 'sidebar-dashboard',
            },
            ...(isAutomationProject ? [
              {
                key: 'templates',
                code: 'TPL',
                icon: 'list-checks',
                label: 'taskTemplates',
                to: this.templatesUrl,
                testId: 'sidebar-templates',
              },
              {
                key: 'schedule',
                code: 'SCH',
                icon: 'clock',
                label: 'schedule',
                to: `${base}/schedule`,
                testId: 'sidebar-schedule',
              },
              {
                key: 'workflows',
                code: 'WF',
                icon: 'git-merge',
                label: 'workflows',
                to: `${base}/workflows`,
                testId: 'sidebar-workflows',
              },
            ] : []),
          ],
        },
        isAutomationProject ? {
          key: 'runtime',
          label: 'navGroupRuntime',
          items: [
            {
              key: 'ee-builds',
              code: 'EE',
              icon: 'package',
              label: 'eeBuilds',
              to: `${base}/ee_builds`,
              testId: 'sidebar-ee-builds',
            },
            {
              key: 'inventory',
              code: 'INV',
              icon: 'server',
              label: 'inventory',
              to: `${base}/inventory`,
              testId: 'sidebar-inventory',
            },
            {
              key: 'environment',
              code: 'VAR',
              icon: 'braces',
              label: 'environment',
              to: `${base}/environment`,
              testId: 'sidebar-environment',
            },
            {
              key: 'execution_environments',
              code: 'IMG',
              icon: 'container',
              label: 'navExecutionEnvironments',
              to: `${base}/execution_environments`,
              testId: 'sidebar-execution-environments',
            },
            {
              key: 'repositories',
              code: 'GIT',
              icon: 'git-branch',
              label: 'repositories',
              to: `${base}/repositories`,
            },
          ],
        } : null,
        {
          key: 'access',
          label: 'navGroupAccess',
          items: [
            ...(isAutomationProject ? [
              {
                key: 'keys',
                code: 'KEY',
                icon: 'key-round',
                label: 'keyStore',
                to: `${base}/keys`,
                testId: 'sidebar-keys',
              },
              {
                key: 'integrations',
                code: 'INT',
                icon: 'plug-zap',
                label: 'integrations',
                to: `${base}/integrations`,
                testId: 'sidebar-integrations',
              },
            ] : []),
            {
              key: 'team',
              code: 'IAM',
              icon: 'users',
              label: 'team',
              to: `${base}/team`,
              testId: 'sidebar-team',
            },
            ...(this.isPro && isAutomationProject ? [
              {
                key: 'runners',
                code: 'RUN',
                icon: 'settings',
                label: 'runners',
                to: `${base}/runners`,
                testId: 'sidebar-runners',
              },
            ] : []),
          ],
        },
      ];

      return groups.filter((group) => group && group.items.length > 0);
    },
  },

  async created() {
    const storedTheme = localStorage.getItem('themeMode');
    if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system') {
      this.themeMode = storedTheme;
    } else if (localStorage.getItem('darkMode') === '1') {
      // Migrate legacy boolean flag to the new tri-state preference.
      this.themeMode = 'dark';
      localStorage.setItem('themeMode', 'dark');
    }
    // Always purge the legacy key so it can't drift back into the
    // application state via rollback / other tabs / stale browser sync.
    if (localStorage.getItem('darkMode') !== null) {
      localStorage.removeItem('darkMode');
    }

    if (window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemThemeListener = () => {
        if (this.themeMode === 'system') {
          this.applyTheme();
        }
      };
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.systemThemeListener);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.systemThemeListener);
      }
    }

    this.applyTheme();

    try {
      await this.loadData();
      this.state = 'success';
    } catch (err) {
      if (err.response && err.response.status === 401) {
        if (this.$route.path !== '/auth/login') {
          await this.$router.replace({
            path: '/auth/login',
            query: { return: this.$route.fullPath },
          });
        }
        this.state = 'success';
        return;
      }

      EventBus.$emit('i-snackbar', {
        color: 'error',
        text: getErrorMessage(err),
      });
      this.state = 'error';
      socket.stop();
    }
  },

  mounted() {
    EventBus.$on('i-snackbar', (e) => {
      this.snackbar = true;
      this.snackbarColor = e.color;
      this.snackbarText = e.text;
    });

    EventBus.$on('i-account-change', async () => {
      await this.loadUserInfo();
    });

    EventBus.$on('i-show-drawer', async () => {
      this.drawer = true;
    });

    EventBus.$on('i-new-project', (e) => {
      setTimeout(() => {
        this.showNewProjectDialogue(e.projectType);
      }, 500);
    });

    EventBus.$on('i-show-task', async (e) => {
      if (parseInt(this.$route.query.t || '', 10) !== e.taskId) {
        const query = { ...this.$route.query, t: e.taskId };
        await this.$router.replace({ query });
        return;
      }

      this.taskId = e.taskId;
      await delay(1);
      this.taskLogDialog = true;
    });

    EventBus.$on('i-open-last-project', async () => {
      await this.trySelectMostSuitableProject();
    });

    EventBus.$on('i-user', async (e) => {
      let text;

      switch (e.action) {
        case 'new':
          text = `User ${e.item.name} created`;
          break;
        case 'edit':
          text = `User ${e.item.name} saved`;
          break;
        case 'delete':
          text = `User ${e.item.name} deleted`;
          break;
        default:
          throw new Error('Unknown project action');
      }

      EventBus.$emit('i-snackbar', {
        color: 'success',
        text,
      });

      if (this.user && e.item.id === this.user.id) {
        await this.loadUserInfo();
      }
    });

    EventBus.$on('i-project', async (e) => {
      let text;

      const project = this.projects.find((p) => p.id === e.item.id) || e.item;
      const projectName = project.name || `#${project.id}`;

      switch (e.action) {
        case 'new':
          text = `Project ${projectName} created`;
          break;
        case 'edit':
          text = `Project ${projectName} saved`;
          break;
        case 'delete':
          text = `Project ${projectName} deleted`;
          break;
        case 'restore':
          break;
        default:
          throw new Error('Unknown project action');
      }

      if (e.action === 'restore') {
        const emptyKeys = (
          await axios({
            method: 'get',
            url: `/api/project/${project.id}/keys`,
            responseType: 'json',
          })
        ).data.filter((k) => k.empty);

        this.restoreProjectResult = {
          projectName,
          emptyKeys: emptyKeys.length,
        };
        this.restoreProjectResultDialog = true;
      } else {
        EventBus.$emit('i-snackbar', {
          color: 'success',
          text,
        });
      }

      await this.loadProjects();

      switch (e.action) {
        case 'new':
        case 'restore':
          await this.selectProject(e.item.id, { new_project: undefined });
          break;
        case 'delete':
          if (this.projectId === e.item.id && this.projects.length > 0) {
            await this.selectProject(this.projects[0].id);
          }
          break;
        default:
          break;
      }
    });
  },

  beforeDestroy() {
    if (this.mediaQuery && this.systemThemeListener) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.systemThemeListener);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.systemThemeListener);
      }
    }
  },

  methods: {
    applyTheme() {
      let dark;
      if (this.themeMode === 'dark') {
        dark = true;
      } else if (this.themeMode === 'light') {
        dark = false;
      } else {
        dark = !!(this.mediaQuery && this.mediaQuery.matches);
      }
      this.$vuetify.theme.dark = dark;
    },

    showNewProjectDialogue(projectType = '') {
      this.newProjectDialog = true;
      this.newProjectType = projectType;
    },

    selectLanguage(lang) {
      localStorage.setItem('lang', lang);
      window.location.reload();
    },

    async onNewProjectDialogueClosed() {
      const query = { ...this.$route.query, new_project: undefined };
      await this.$router.replace({ query });
    },

    async onTaskLogDialogClosed() {
      const query = { ...this.$route.query, t: undefined };
      await this.$router.replace({ query });
    },

    async loadData() {
      await this.loadUserInfo();

      // Activate session and start socket only after confirming user is authenticated
      socket.setSessionActive(true);
      if (!socket.isRunning()) {
        socket.start();
      }

      await this.loadProjects();

      // try to find project and switch to it if URL not pointing to any project
      if (
        this.$route.path === '/'
        || this.$route.path === '/project'
        || this.$route.path.startsWith('/project/')
      ) {
        await this.trySelectMostSuitableProject();
      }

      // display task dialog if query param t specified
      if (this.$route.query.t) {
        const taskId = parseInt(this.$route.query.t || '', 10);
        if (taskId) {
          EventBus.$emit('i-show-task', { taskId });
        }
      }

      if ((this.projects || []).length > 0 && this.$route.query.new_project != null) {
        EventBus.$emit('i-new-project', { projectType: this.$route.query.new_project });
      }
    },

    async trySelectMostSuitableProject() {
      if (this.projects.length === 0) {
        if (this.$route.path !== '/project/new') {
          await this.$router.push({ path: '/project/new' });
        }
        return;
      }

      let projectId;

      if (this.projectId) {
        projectId = this.projectId;
      }

      if (
        (projectId == null || !this.projects.some((p) => p.id === projectId))
        && localStorage.getItem('projectId')
      ) {
        projectId = parseInt(localStorage.getItem('projectId'), 10);
      }

      if (projectId == null || !this.projects.some((p) => p.id === projectId)) {
        projectId = this.projects[0].id;
      }

      if (projectId != null) {
        await this.selectProject(projectId);
      }
    },

    async selectProject(projectId, overriderQuery = {}) {
      this.userRole = (
        await axios({
          method: 'get',
          url: `/api/project/${projectId}/role`,
          responseType: 'json',
        })
      ).data;

      localStorage.setItem('projectId', projectId);
      if (this.projectId === projectId) {
        return;
      }

      let query = {};

      switch (this.$route.path) {
        case '/project/new':
          query.new_project = '';
          break;
        default:
          break;
      }

      query = {
        ...query,
        ...overriderQuery,
      };

      await this.$router.push({
        path: `/project/${projectId}${window.location.search}`,
        query,
      });
    },

    async loadProjects() {
      this.projects = (
        await axios({
          method: 'get',
          url: '/api/projects',
          responseType: 'json',
        })
      ).data;
    },

    async loadUserInfo() {
      this.user = (
        await axios({
          method: 'get',
          url: '/api/user',
          responseType: 'json',
        })
      ).data;

      this.systemInfo = (
        await axios({
          method: 'get',
          url: '/api/info',
          responseType: 'json',
        })
      ).data;
    },

    getProjectColor(projectData) {
      const i = this.projects.length - this.projects.findIndex((p) => p.id === projectData.id);
      return PROJECT_COLORS[i % PROJECT_COLORS.length];
    },

    getProjectInitials(projectData) {
      const parts = projectData.name.split(/\s/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return parts[0].substr(0, 2).toUpperCase();
    },

    async restoreProject() {
      const f = document.createElement('input');
      f.setAttribute('type', 'file');
      f.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = async (ev) => {
            const fileContent = ev.target.result;
            try {
              await axios.post('/api/projects/restore', fileContent).then(async (payload) => {
                this.$router.push({ path: `/project/${payload.data.id}/history` });
                this.state = 'success';
                await this.loadProjects();
              });
            } catch (err) {
              EventBus.$emit('i-snackbar', {
                color: 'error',
                text: getErrorMessage(err),
              });
            }
          };
          reader.readAsText(file);
        }
      });
      f.click();
    },

    async signOut() {
      this.snackbar = false;
      this.snackbarColor = '';
      this.snackbarText = '';

      try {
        await axios({
          method: 'post',
          url: '/api/auth/logout',
          responseType: 'json',
        });

        socket.setSessionActive(false);
        socket.stop();

        if (this.$route.path !== '/auth/login') {
          await this.$router.push({ path: '/auth/login' });
          this.state = 'success';
        }
      } catch (err) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(err),
        });
      }
    },

    refreshPage() {
      const { location } = document;
      document.location = location;
    },
  },
};
</script>
